import { Component, inject, Renderer2, signal, PLATFORM_ID } from '@angular/core';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { Meta, Title } from '@angular/platform-browser';
import { Api } from '../../services/api';
import { catchError, distinctUntilChanged, of, tap, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ArticlesItems, GetArticlesParam } from '../../interface/articles';
import { ActivatedRoute, RouterLink, Router } from '@angular/router';
import { DatePipe, DecimalPipe, isPlatformBrowser} from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { WidgetFeatured } from '../widget-featured/widget-featured';
import { Widgets } from '../widgets/widgets';
import { Canonical } from '../../services/canonical';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageNotFound } from '../page-not-found/page-not-found';

@Component({
  selector: 'app-articles',
  imports: [
    Navbar,
    Footer,
    WidgetFeatured,
    DecimalPipe,
    Widgets,
    PageNotFound,
    RouterLink,
    DatePipe,
    NgxSkeletonLoaderModule
  ],
  templateUrl: './articles.html',
  styleUrl: './articles.css',
})
export class Articles {
  title = inject(Title);
  meta = inject(Meta);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  isLoading = signal(false);
  articles = signal<ArticlesItems[]>([]);
  private http = inject(HttpClient);
  private api = inject(Api);
  private canonical = inject(Canonical);

  isUndefined = signal(false);
  results = signal(0);
  limit = signal(3);
  page = signal(1);
  prevBtn = signal(false);
  nextBtn = signal(false);
  prevPage = signal(0);
  nextPage = signal(0);

  private platformId = inject(PLATFORM_ID);

  private renderFacebookShareButton(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const parse = () => {
      const fb = (window as any).FB;
      if (typeof fb !== 'undefined' && fb.XFBML) {
        fb.XFBML.parse();
        return true;
      }
      return false;
    };

    setTimeout(() => {
      if (parse()) return;

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (parse() || attempts >= 20) {
          clearInterval(interval);
        }
      }, 200);
    }, 100);
  }

  private renderTwitterShareButton(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const parse = () => {
      const twttr = (window as any).twttr;
      if (typeof twttr !== 'undefined' && twttr.widgets) {
        twttr.widgets.load();
        return true;
      }
      return false;
    };

    setTimeout(() => {
      if (parse()) return;

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (parse() || attempts >= 20) {
          clearInterval(interval);
        }
      }, 200);
    }, 100);
  }

  prev() : void {
    this.router.navigate(['/articles', this.limit(), this.prevPage()]);
  }

  next() : void {
    this.router.navigate(['/articles', this.limit(), this.nextPage()]);
  }

  private initialize(obj: GetArticlesParam) : void {
    this.isLoading.set(true);
    this.isUndefined.set(false);
   
    this.api.getArticles(obj)
    .pipe(
      timeout(10000),
      catchError(() => of({
        results: 0,
        page: {
          previous: false,
          next: false
        },
        canonical: null,
        siteTitle: null,
        meta: null,
        items: []
      })),
      tap(() => this.isLoading.set(false))
    ).subscribe(obj => {
      if(obj.items.length == 0){
        this.isUndefined.set(true);
      }else{
        this.results.set(obj.results);
        this.canonical.setCanonicalUrl(obj?.canonical!);
        this.title.setTitle(obj?.siteTitle!);
        this.meta.addTags(JSON.parse(obj?.meta!));
        this.articles.set(obj.items);
        this.prevBtn.set(obj.page.previous);
        this.nextBtn.set(obj.page.next);
        this.renderFacebookShareButton();
        this.renderTwitterShareButton();
      }
    });
  }

  request = toSignal(this.http.get<any>(`${environment.apiUrl}/articles/${this.limit()}/${this.page()}`));

  ngOnInit() : void {
    this.activatedRoute.paramMap
    .pipe(
      distinctUntilChanged()
    ).subscribe(param => {
      const limit = param.get('limit');
      const page = param.get('page');
      
      this.limit.set(Number(limit));
      this.page.set(Number(page));
      this.prevPage.set(Number(page) - 1);
      this.nextPage.set(Number(page) + 1);

      this.initialize({
        limit: this.limit(),
        page: this.page()
      });
    });
  }
}
